import React, { useCallback, useMemo, useState } from "react";
import { Layers, Moon, RefreshCw, Sun, TriangleAlert, WifiOff } from "lucide-react";

import {
  ColGroup,
  DateTime,
  Delta,
  FillerRows,
  TABLE_COL_COUNT,
  dir,
  money,
  serverTime,
  signed,
  sumBy,
} from "./reportCommon";
import useInvestorReport from "./useInvestorReport";
import useDevTheme from "./useDevTheme";
import "./InvestorReport.css";
import "./reportBoard.css";

/**
 * /all/investment/detail — ONE CONTAINER, CONDENSED.
 *
 * The same report as /all/investment/reports, cut down to the figures asked
 * for, and its dropdown lists every container the same way — private ones
 * included — because it reads the same /all-containers endpoint. It is public,
 * and that consequence is written out on the route in App.js.
 *
 * A DISABLED CONTAINER IS NOT IN THE LIST, on either feed. `enabled` is the one
 * flag that does not vary by audience: containerScope drops a disabled book for
 * everybody, so neither this page nor a signed-in developer on it can select
 * one. Only /dev/containers still lists them.
 *
 * NO "ALL" IN THE SELECTOR, by request. The combined-across-containers view has
 * its own page; here a selection is always one book, so the reserved value is
 * filtered out of the list rather than left in to produce a view this page has
 * no layout for.
 *
 * WHAT IS GONE, relative to the full report:
 *   Total deposited / withdrawn / Net deposits / Margin used — capital flows and
 *     margin headroom, not performance. The full report still has them.
 *   Opening balance — asked to be removed with them.
 *   Combined statistics — the today/week/month/year/overall buckets.
 *   Settled and unsettled — the reconciliation SECTION. The two tiles stay, by
 *     request; the table that breaks them down does not.
 *   per account: Equity, Deposits, Withdrawals, Net deposits, Opening balance,
 *     Margin used, Free margin, Margin level, Settled and Unsettled.
 *   per account: the same five period buckets, per broker.
 *
 * WHAT IS ADDED: session P&L on every account, with its trade count.
 *
 * NEITHER FIGURE IS RECOMPUTED HERE, which is the point. `periods.today` IS the
 * session: report.js builds its bounds from tradingDay, whose day runs 02:00 to
 * 02:00. `periods.overall` has no calendar boundary and the trades feeding a
 * per-account panel start at the PAIR date (see inceptionUnix in mt5/report.js),
 * so it is already "since the period began" — Total P&L is a rename, not new
 * arithmetic. Recomputing either would have been a second definition free to
 * drift from the one the full report shows.
 *
 * LIVE POSITIONS AND THE TRADE LOG ARE THE REPORT'S OWN TABLES, copied
 * character for character rather than reimplemented, so they cannot look or
 * behave differently here. `maxRows` is computed the same way for the same
 * reason: it keeps two accounts' tables level so their headers and Total rows
 * share one line.
 */

/** A figure with its trade count underneath. */
function CountFigure({ label, value, cls, trades, known = true }) {
  return (
    <div className="ir-fig">
      <span className="ir-fig-label">{label}</span>
      <b className={`ir-fig-value ${known ? cls || "" : "ir-muted"}`}>{known ? value : "—"}</b>
      <em className="ir-fig-sub">
        {!known ? "not read yet" : trades == null ? "—" : `${trades} trade${trades === 1 ? "" : "s"}`}
      </em>
    </div>
  );
}

/**
 * `poolBreakdown.unsettled`, NOT `combined.unsettledPnl`.
 *
 * The two differ by whatever the period carried in and has not banked, and both
 * payload builders derive the breakdown from the identity
 * carriedIn + earned - banked, so it is the one that reconciles with the tile
 * beside it. The fallback is for a payload that predates the field.
 */
function unsettledOf(c) {
  if (c && c.poolBreakdown && c.poolBreakdown.unsettled != null) return c.poolBreakdown.unsettled;
  return (c && c.unsettledPnl) || 0;
}

/**
 * The container's session figure, with its trade count.
 *
 * `periods.today` is the trading day, 02:00 to 02:00 - not the calendar day -
 * because report.js takes every period bound from mt5/tradingDay. Returning a
 * shape rather than a number so the count travels with the amount it belongs
 * to; a count fetched separately from its figure is how the two come to
 * disagree.
 */
function sessionOf(c) {
  const t = (c && c.periods && c.periods.today) || null;
  return { pnl: (t && t.pnl) || 0, trades: t ? t.trades : null };
}

function DetailAccount({ acc, maxRows }) {
  const ccy = acc.currency || "USD";
  const session = (acc.periods && acc.periods.today) || { pnl: 0, trades: 0 };
  const total = (acc.periods && acc.periods.overall) || { pnl: acc.closedPnl || 0, trades: null };

  return (
    <section className="ird-account" aria-labelledby={`d-${acc.id}`}>
      <header className="ird-acc-head">
        <div className="ird-acc-id">
          <span className="irb-card-badge" aria-hidden="true">
            {(acc.label || "??").slice(0, 2).toUpperCase()}
          </span>
          <div>
            <h3 id={`d-${acc.id}`}>{acc.label}</h3>
            <p>
              #{acc.login} · {acc.platform || "MT5"}
            </p>
          </div>
        </div>
        {!acc.connected && (
          <span className="ir-delta ir-down">
            <WifiOff size={13} aria-hidden="true" /> offline
          </span>
        )}
      </header>

      <div className="ir-comb-figs">
        <div className="ir-fig">
          <span className="ir-fig-label">Balance</span>
          <b className="ir-fig-value">{money(acc.balance, ccy)}</b>
        </div>
        <CountFigure
          label="Session P&amp;L"
          value={signed(session.pnl, ccy)}
          cls={`ir-${dir(session.pnl)}`}
          trades={session.trades}
        />
        <CountFigure
          label="Total P&amp;L"
          value={signed(total.pnl, ccy)}
          cls={`ir-${dir(total.pnl)}`}
          trades={total.trades}
        />
        <div className="ir-fig">
          <span className="ir-fig-label">Lots</span>
          <b className="ir-fig-value">{(acc.lots || 0).toFixed(2)}</b>
        </div>
        <div className="ir-fig">
          <span className="ir-fig-label">Floating P&amp;L</span>
          <b className={`ir-fig-value ir-${dir(acc.floating)}`}>{signed(acc.floating, ccy)}</b>
        </div>
      </div>

      {/* --------------------------- live positions -------------------------- */}
      <div className="ir-block">
        <div className="ir-block-head">
          <h3>Live positions</h3>
          <span className="ir-block-note">
            {(acc.positions || []).length} open · {(acc.lots || 0).toFixed(2)} lots
          </span>
        </div>

        {/* The bare message only when NEITHER account has positions — then both
            panels render it and stay level. If the panel beside this one has
            rows, render the table skeleton instead so the two headers, row
            areas and Total rows still line up across the report. */}
        {(acc.positions || []).length === 0 && maxRows.positions === 0 ? (
          <p className="ir-empty">No open positions.</p>
        ) : (
          <>
            <div className="ir-tbl-wrap">
              <table className="ir-tbl ir-tbl-pos">
                <caption className="ir-sr">Open positions for {acc.label}.</caption>
                <ColGroup />
                <thead>
                  <tr>
                    <th scope="col" className="ir-idx">#</th>
                    <th scope="col">Symbol</th>
                    <th scope="col">Side</th>
                    <th scope="col" className="ir-r">Lots</th>
                    <th scope="col" className="ir-r">Open</th>
                    <th scope="col" className="ir-r">Current</th>
                    <th scope="col" className="ir-r">Opened</th>
                    <th scope="col" className="ir-r">Swap</th>
                    <th scope="col" className="ir-r">P&amp;L</th>
                  </tr>
                </thead>
                <tbody>
                  {acc.positions.map((p, i) => (
                    <tr key={p.ticket}>
                      <td className="ir-idx">{i + 1}</td>
                      <td>
                        <span className="ir-sym">{p.symbol}</span>
                        <span className="ir-ticket">#{p.ticket}</span>
                      </td>
                      <td>
                        <span className={`ir-pill ir-pill-${(p.type || "").toLowerCase()}`}>{p.type}</span>
                      </td>
                      <td className="ir-r ir-num">{p.lots.toFixed(2)}</td>
                      <td className="ir-r ir-num">{p.openPrice.toFixed(p.digits)}</td>
                      <td className="ir-r ir-num ir-bold">{p.currentPrice.toFixed(p.digits)}</td>
                      <td className="ir-r">
                        <DateTime unix={p.openTime} />
                      </td>
                      <td className={`ir-r ir-num ir-xs ir-${dir(p.swap)}`}>{signed(p.swap, ccy)}</td>
                      <td className={`ir-r ir-num ir-bold ir-${dir(p.net)}`}>{signed(p.net, ccy)}</td>
                    </tr>
                  ))}
                  {acc.positions.length === 0 && (
                    <tr className="ir-none">
                      <td colSpan={TABLE_COL_COUNT}>No open positions.</td>
                    </tr>
                  )}
                  {/* The message row above already occupies one row's height. */}
                  <FillerRows
                    count={maxRows.positions - Math.max(acc.positions.length, 1)}
                    cols={TABLE_COL_COUNT}
                  />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3">Total</td>
                    <td className="ir-r ir-num">{(acc.lots || 0).toFixed(2)}</td>
                    <td />
                    <td />
                    <td />
                    {/* Swap carried by the open positions. */}
                    <td className={`ir-r ir-num ir-xs ir-${dir(sumBy(acc.positions, "swap"))}`}>
                      {signed(sumBy(acc.positions, "swap"), ccy)}
                    </td>
                    <td className={`ir-r ir-num ir-${dir(acc.floating)}`}>{signed(acc.floating, ccy)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <ul className="ir-rows">
              {/* The table above is hidden in the narrow card view, so the
                  message has to exist here too or the block reads as blank. */}
              {acc.positions.length === 0 && <li className="ir-row-none">No open positions.</li>}
              {acc.positions.map((p, i) => (
                <li key={p.ticket}>
                  <span className="ir-row-top">
                    <span className="ir-row-idx">{i + 1}</span>
                    <span className="ir-sym">{p.symbol}</span>
                    <span className={`ir-pill ir-pill-${(p.type || "").toLowerCase()}`}>{p.type}</span>
                    <span className="ir-num ir-xs ir-muted">{p.lots.toFixed(2)} lots</span>
                    <b className={`ir-num ir-row-pnl ir-${dir(p.net)}`}>{signed(p.net, ccy)}</b>
                  </span>
                  <span className="ir-row-mid ir-num">
                    {p.openPrice.toFixed(p.digits)} <i aria-hidden="true">→</i>{" "}
                    <b>{p.currentPrice.toFixed(p.digits)}</b>
                  </span>
                  <span className="ir-row-foot">
                    <em>
                      Opened {serverTime(p.openTime).date} {serverTime(p.openTime).time}
                    </em>
                    <em className={`ir-${dir(p.swap)}`}>swap {signed(p.swap, ccy)}</em>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* ------------------------------ trade log --------------------------- */}
      <div className="ir-block ir-block-log">
        <div className="ir-block-head">
          <h3>Trade log</h3>
          <span className="ir-block-note">
            last {(acc.log || []).length} closed · P&amp;L net of commission &amp; swap
          </span>
        </div>

        {/* As with positions: only drop to a bare message when NEITHER account
            has closed trades, so the two logs stay aligned. */}
        {(acc.log || []).length === 0 && maxRows.log === 0 ? (
          <p className="ir-empty">No closed trades in the reporting window.</p>
        ) : (
          <>
            <div className="ir-tbl-wrap ir-log-scroll">
              <table className="ir-tbl ir-tbl-log">
                <caption className="ir-sr">Closed trades for {acc.label}.</caption>
                <ColGroup />
                <thead>
                  <tr>
                    <th scope="col" className="ir-idx">#</th>
                    <th scope="col">Symbol</th>
                    <th scope="col">Side</th>
                    <th scope="col" className="ir-r">Lots</th>
                    <th scope="col" className="ir-r">Open</th>
                    <th scope="col" className="ir-r">Close</th>
                    <th scope="col" className="ir-r">Opened</th>
                    <th scope="col" className="ir-r">Closed</th>
                    <th scope="col" className="ir-r">P&amp;L / comm.</th>
                  </tr>
                </thead>
                <tbody>
                  {acc.log.map((t, i) => (
                    <tr key={t.ticket}>
                      <td className="ir-idx">{i + 1}</td>
                      <td>
                        <span className="ir-sym">{t.symbol}</span>
                        <span className="ir-ticket">#{t.ticket}</span>
                      </td>
                      <td>
                        <span className={`ir-pill ir-pill-${(t.type || "").toLowerCase()}`}>{t.type}</span>
                      </td>
                      <td className="ir-r ir-num">{t.lots.toFixed(2)}</td>
                      <td className="ir-r ir-num">{t.openPrice.toFixed(t.digits)}</td>
                      <td className="ir-r ir-num">{t.closePrice.toFixed(t.digits)}</td>
                      <td className="ir-r">
                        <DateTime unix={t.openTime} />
                      </td>
                      <td className="ir-r">
                        <DateTime unix={t.closeTime} />
                      </td>
                      {/* Commission stacked under the P&L it was deducted from,
                          rather than taking a column the positions table would
                          have to leave empty. Always rendered, including at
                          zero, so every row is the same height and the two
                          panels' Total rows stay on one line. */}
                      <td className="ir-r">
                        <span className={`ir-num ir-bold ir-${dir(t.pnl)}`}>{signed(t.pnl, ccy)}</span>
                        <span className={`ir-cell-sub ir-num ir-${dir(t.commission)}`}>
                          comm {signed(t.commission, ccy)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {acc.log.length === 0 && (
                    <tr className="ir-none">
                      <td colSpan={TABLE_COL_COUNT}>No closed trades in the reporting window.</td>
                    </tr>
                  )}
                  {/* The message row above already occupies one row's height. */}
                  <FillerRows count={maxRows.log - Math.max(acc.log.length, 1)} cols={TABLE_COL_COUNT} />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3">Shown trades</td>
                    <td />
                    <td />
                    <td />
                    <td />
                    <td />
                    <td className="ir-r">
                      <span className={`ir-num ir-${dir(acc.log.reduce((a, x) => a + x.pnl, 0))}`}>
                        {signed(acc.log.reduce((a, x) => a + x.pnl, 0), ccy)}
                      </span>
                      <span className={`ir-cell-sub ir-num ir-${dir(sumBy(acc.log, "commission"))}`}>
                        comm {signed(sumBy(acc.log, "commission"), ccy)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <ul className="ir-rows ir-rows-log">
              {acc.log.length === 0 && (
                <li className="ir-row-none">No closed trades in the reporting window.</li>
              )}
              {acc.log.map((t, i) => (
                <li key={t.ticket}>
                  <span className="ir-row-top">
                    <span className="ir-row-idx">{i + 1}</span>
                    <span className="ir-sym">{t.symbol}</span>
                    <span className={`ir-pill ir-pill-${(t.type || "").toLowerCase()}`}>{t.type}</span>
                    <span className="ir-num ir-xs ir-muted">{t.lots.toFixed(2)} lots</span>
                    <b className={`ir-num ir-row-pnl ir-${dir(t.pnl)}`}>{signed(t.pnl, ccy)}</b>
                  </span>
                  <span className="ir-row-mid ir-num">
                    {t.openPrice.toFixed(t.digits)} <i aria-hidden="true">→</i> {t.closePrice.toFixed(t.digits)}
                  </span>
                  <span className="ir-row-foot">
                    <em>
                      In {serverTime(t.openTime).date} {serverTime(t.openTime).time}
                    </em>
                    <em>
                      Out {serverTime(t.closeTime).date} {serverTime(t.closeTime).time}
                    </em>
                    <em className={`ir-${dir(t.commission)}`}>comm {signed(t.commission, ccy)}</em>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * `allContainers` — WHICH CONTAINERS THE DROPDOWN OFFERS.
 *
 *   true   /all/investment/detail — reads /api/investor-report/all-containers,
 *          a second mount of the same handler that forces `visibleTo:
 *          "developer"`. Every ENABLED book, private ones included, to anybody.
 *   false  /investment/detail — reads the ordinary /api/investor-report, which
 *          picks the audience from the request: an anonymous visitor is offered
 *          only the `publicVisible` ones.
 *
 * Both are enabled-only. That flag is not an audience question.
 *
 * ONE COMPONENT, TWO FEEDS, because the difference really is only the list. Two
 * copies of this page would be two things to keep in step, and the figures are
 * the part nobody would notice drifting.
 *
 * THE NUANCE WORTH KNOWING, and it is deliberate rather than a gap: on the
 * public feed a SIGNED-IN DEVELOPER is still offered the private containers,
 * because `audienceOf` reads their session. That is exactly how /investor-report
 * has always behaved - containerScope's header calls it out - so this page
 * matches the report it is cut down from. An anonymous visitor, which is who
 * that page is for, never sees them.
 */
/**
 * `endpoint` — READ A FIXED FEED, AND OFFER NO SELECTOR.
 *
 * /fund/investment/detail passes it. That feed serves ONE container chosen in
 * code and ignores `?container=` entirely, so a dropdown would be a control
 * that cannot change anything — the selector is hidden rather than drawn
 * disabled.
 *
 * Expressed as its own prop rather than another value of `allContainers`,
 * because it changes two different things: which feed is read AND whether this
 * page has a selector at all. InvestorReport.js makes the same distinction for
 * the same reason.
 */
export default function ContainerDetail({ allContainers = true, endpoint }) {
  const { dark, toggle } = useDevTheme();
  const theme = dark ? "dark" : "light";

  /* In the URL, like the report's own selector: the choice is per viewer, a
     particular container becomes a link somebody can be sent, and a reload comes
     back to the same book instead of jumping to the default. */
  const [container, setContainer] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("container") || "";
  });

  const choose = useCallback((next) => {
    setContainer(next);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (next) url.searchParams.set("container", next);
    else url.searchParams.delete("container");
    /* replaceState, so flicking through containers does not fill the Back button
       with steps nobody wants to walk back through. */
    window.history.replaceState({}, "", url);
  }, []);

  const selectable = !endpoint;
  const { report, error, live, polling, checkedAt, refresh } = useInvestorReport(
    endpoint || (allContainers ? "/api/investor-report/all-containers" : "/api/investor-report"),
    /* No container parameter on a fixed feed: it would be ignored by the server
       and would only make the URL imply a choice the page does not have. */
    { container: selectable ? container : "" }
  );

  const combined = (report && report.combined) || null;
  /* Memoised for the same reason InvestorReport.js memoises it: a fresh array
     every render would invalidate the `maxRows` memo below on every tick, and
     that memo exists to keep two accounts' tables level. */
  const accounts = useMemo(() => (report && report.accounts) || [], [report]);
  const ccy = (combined && combined.currency) || "USD";

  /* ALL means nothing here, so it is not offered. The server can still answer
     with it if somebody types ?container=all, which is why the notice below
     exists instead of a blank page. */
  const list = ((report && report.containers) || []).filter((c) => c.id !== "all");
  const selection = (report && report.selection) || null;
  const selected = selection ? selection.containerId || "" : container;
  const showingAll = Boolean(selection && selection.mode === "all");

  const maxRows = useMemo(
    () => ({
      positions: Math.max(0, ...accounts.map((a) => (a.positions || []).length)),
      log: Math.max(0, ...accounts.map((a) => (a.log || []).length)),
    }),
    [accounts]
  );

  return (
    <div className={`ir-root ir-theme-${theme}`} data-ir-theme={theme}>
      <header className="ir-topbar">
        <div className="ir-topbar-inner">
          <div className="irb-head">
            <img src="/firstbench-logo.png" alt="" className="ir-brand-logo" />
            <div className="irb-title">
              <h1>{(selection && selection.containerName) || "Container report"}</h1>
            <p>
                Session P&amp;L is the 02:00 to 02:00 trading day. Total P&amp;L is measured from
                this container&apos;s period start.
              </p>
            </div>
          </div>
          <div className="ir-topbar-right">
            {selectable && (
            <div className="ird-select">
              <label htmlFor="ird-container">Container</label>
              <select id="ird-container" value={selected} onChange={(e) => choose(e.target.value)}>
                {list.length === 0 && <option value="">&mdash;</option>}
                {list.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {/* Unreachable: the server does not send a disabled book to
                        either feed. Left in so one would be labelled rather
                        than blending into the list if it ever were. */}
                    {c.enabled === false ? " (disabled)" : ""}
                    {c.publicVisible === false ? " · not public" : ""}
                  </option>
                ))}
              </select>
            </div>
            )}
            <span className="irb-checked">
              {live ? "live" : polling ? "polling" : "idle"}
              {checkedAt ? ` · ${Math.round((Date.now() - checkedAt) / 1000)}s ago` : ""}
            </span>
            <button type="button" className="ir-btn ir-btn-icon" onClick={refresh} aria-label="Refresh">
              <RefreshCw size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ir-btn ir-btn-icon"
              onClick={toggle}
              aria-label={dark ? "Light theme" : "Dark theme"}
            >
              {dark ? <Sun size={15} aria-hidden="true" /> : <Moon size={15} aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      <main className="ir-main">
        {error && (
          <p className="irb-error">
            <TriangleAlert size={15} aria-hidden="true" />
            <span>{String((error && error.message) || error)}</span>
          </p>
        )}

        {showingAll && (
          <p className="irb-error">
            <TriangleAlert size={15} aria-hidden="true" />
            <span>This page shows one container at a time. Choose one from the selector above.</span>
          </p>
        )}

        {!report && !error && <p className="ir-empty">Loading&hellip;</p>}

        {/* ------------------------- NO LIVE SNAPSHOT -------------------------
            The route serves one of TWO shapes. With a poller holding the
            container it returns the snapshot - `combined` and `accounts`, with
            positions and logs. With nothing polling it returns `card` instead:
            the same figures from the last poll, and no positions or logs,
            because those only exist in a live snapshot.

            Without this branch the page rendered NOTHING for a container nobody
            is polling - it reads `report.combined` and there is none. Found by a
            test whose fixture had no poller, which is also the normal state of
            any container just after a restart. /investor-report has the same
            fallback; this is the condensed version of it. */}
        {!combined && report && report.card && (
          <>
            {report.message && (
              <p className="irb-error">
                <TriangleAlert size={15} aria-hidden="true" />
                <span>{report.message}</span>
              </p>
            )}

            <section className="ir-combined irb-overall" aria-labelledby="ird-stored">
              <div className="irb-book-head">
                <span className="ir-eyebrow">
                  <Layers size={13} aria-hidden="true" /> Last recorded ·{" "}
                  {report.card.connectedCount}/{report.card.accountCount} live
                </span>
                <h2 id="ird-stored">{report.card.name}</h2>
              </div>
              <div className="ir-comb-figs">
                <div className="ir-fig">
                  <span className="ir-fig-label">Balance</span>
                  <b className="ir-fig-value">{money(report.card.balance, ccy)}</b>
                </div>
                <CountFigure
                  label="Session P&amp;L"
                  value={signed(report.card.sessionPnl, ccy)}
                  cls={`ir-${dir(report.card.sessionPnl)}`}
                  trades={report.card.sessionTrades}
                  known={report.card.ready !== false}
                />
                <CountFigure
                  label="Total P&amp;L"
                  value={signed(report.card.closedPnl, ccy)}
                  cls={`ir-${dir(report.card.closedPnl)}`}
                  trades={report.card.pairTrades}
                  known={report.card.ready !== false}
                />
                <div className="ir-fig">
                  <span className="ir-fig-label">Floating P&amp;L</span>
                  <b className={`ir-fig-value ir-${dir(report.card.floating)}`}>
                    {signed(report.card.floating, ccy)}
                  </b>
                </div>
                <div className="ir-fig">
                  <span className="ir-fig-label">Unsettled P&amp;L</span>
                  <b className={`ir-fig-value ir-${dir(report.card.unsettled)}`}>
                    {signed(report.card.unsettled, ccy)}
                  </b>
                </div>
                <div className="ir-fig">
                  <span className="ir-fig-label">Settled P&amp;L</span>
                  <b className={`ir-fig-value ir-${dir(report.card.settled)}`}>
                    {signed(report.card.settled, ccy)}
                  </b>
                </div>
              </div>
            </section>

            <div className="ird-accounts">
              {(report.card.accounts || []).map((a) => (
                <section className="ird-account" key={a.login}>
                  <header className="ird-acc-head">
                    <div className="ird-acc-id">
                      <span className="irb-card-badge" aria-hidden="true">
                        {(a.label || "??").slice(0, 2).toUpperCase()}
                      </span>
                      <div>
                        <h3>{a.label || `#${a.login}`}</h3>
                        <p>#{a.login}</p>
                      </div>
                    </div>
                  </header>
                  <div className="ir-comb-figs">
                    <div className="ir-fig">
                      <span className="ir-fig-label">Balance</span>
                      <b className="ir-fig-value">{money(a.balance, ccy)}</b>
                    </div>
                    <CountFigure
                      label="Session P&amp;L"
                      value={signed(a.sessionPnl, ccy)}
                      cls={`ir-${dir(a.sessionPnl)}`}
                      trades={a.sessionTrades}
                      known={report.card.ready !== false}
                    />
                    <CountFigure
                      label="Total P&amp;L"
                      value={signed(a.closedPnl, ccy)}
                      cls={`ir-${dir(a.closedPnl)}`}
                      trades={a.pairTrades}
                      known={report.card.ready !== false}
                    />
                    <div className="ir-fig">
                      <span className="ir-fig-label">Lots</span>
                      <b className="ir-fig-value">
                        {a.lots == null ? "—" : Number(a.lots).toFixed(2)}
                      </b>
                    </div>
                    <div className="ir-fig">
                      <span className="ir-fig-label">Floating P&amp;L</span>
                      <b className={`ir-fig-value ir-${dir(a.floating)}`}>{signed(a.floating, ccy)}</b>
                    </div>
                  </div>
                  {/* Said rather than left blank: an empty positions table and an
                      empty log would read as "nothing traded" when the truth is
                      that nobody is connected to look. */}
                  <p className="ir-empty">
                    Live positions and the trade log need a running poller for this container.
                  </p>
                </section>
              ))}
            </div>
          </>
        )}


        {combined && !showingAll && (
          <>
            <section className="ir-combined" aria-labelledby="ird-comb">
              <div className="ir-comb-hero">
                <span className="ir-eyebrow">
                  <Layers size={13} aria-hidden="true" /> Combined · {combined.connectedCount}/
                  {combined.accountCount} live
                </span>
                <h2 id="ird-comb" className="ir-hero-figure">
                  {money(combined.balance, ccy, 2)}
                </h2>
                <div className="ir-hero-deltas">
                  <Delta value={combined.returnPct} />
                  <span className="ir-hero-abs">
                    {signed(combined.closedPnl, ccy, 2)} total P&amp;L
                  </span>
                </div>
              </div>

              {/* SEVEN TILES. The capital flows, margin used and the opening
                  balance are gone by request. Unsettled and Settled stay even
                  though the section that breaks them down does not - so they are
                  the only two figures on this page with nothing beneath them to
                  reconcile against, which is why they are last.
                  `closedPnl` is labelled Total P&L here: the figure is
                  unchanged, only the name.

                  ORDERED LIKE THE ACCOUNT PANELS BELOW - balance, session,
                  total - so a reader scanning down the page meets the same three
                  figures in the same sequence at both levels. */}
              <div className="ir-comb-figs">
                <div className="ir-fig">
                  <span className="ir-fig-label">Balance</span>
                  <b className="ir-fig-value">{money(combined.balance, ccy)}</b>
                </div>
                {/* THE CONTAINER'S SESSION, read not recomputed.
                    `combined.periods.today` IS the 02:00-02:00 trading day:
                    report.js builds every period bound from mt5/tradingDay, and
                    combine.js overwrites the combined statistics from the stored
                    deals of THIS container. So this is the same definition as
                    `sessionPnl` on the overview board and the same as the
                    account panels below - verified equal against the board for
                    the same container - rather than a fourth place that decides
                    what a session is. */}
                <CountFigure
                  label="Session P&amp;L"
                  value={signed(sessionOf(combined).pnl, ccy)}
                  cls={`ir-${dir(sessionOf(combined).pnl)}`}
                  trades={sessionOf(combined).trades}
                />
                <div className="ir-fig">
                  <span className="ir-fig-label">Total P&amp;L</span>
                  <b className={`ir-fig-value ir-${dir(combined.closedPnl)}`}>
                    {signed(combined.closedPnl, ccy)}
                  </b>
                </div>
                <div className="ir-fig">
                  <span className="ir-fig-label">Floating P&amp;L</span>
                  <b className={`ir-fig-value ir-${dir(combined.floating)}`}>
                    {signed(combined.floating, ccy)}
                  </b>
                </div>
                <div className="ir-fig">
                  <span className="ir-fig-label">Open positions</span>
                  <b className="ir-fig-value">{combined.openPositions}</b>
                  <em className="ir-fig-sub">{(combined.lots || 0).toFixed(2)} lots</em>
                </div>
                <div className="ir-fig">
                  <span className="ir-fig-label">Unsettled P&amp;L</span>
                  <b className={`ir-fig-value ir-${dir(unsettledOf(combined))}`}>
                    {signed(unsettledOf(combined), ccy)}
                  </b>
                </div>
                <div className="ir-fig">
                  <span className="ir-fig-label">Settled P&amp;L</span>
                  <b className={`ir-fig-value ir-${dir(combined.totalSettledPnl)}`}>
                    {signed(combined.totalSettledPnl, ccy)}
                  </b>
                </div>
              </div>
            </section>

            {accounts.length === 0 ? (
              <p className="ir-empty">No account is recording in this container.</p>
            ) : (
              /* Side by side where there is room. The report solves this with a
                 fixed two-column split plus a broker switcher below its
                 breakpoint; this page has no switcher, so the grid stacks
                 instead and both brokers stay on screen at every width. */
              <div className="ird-accounts">
                {accounts.map((a) => (
                  <DetailAccount key={a.id} acc={a} maxRows={maxRows} />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
